import { OfferCues } from '../../../core/offer-cues.js';
import { RECEIVER_LAST_AD_BREAK_KEY, readLastBreakEndedAt, resolveDeviceId, } from '../../../core/receiver.js';
import { RECEIVER_ERROR_CODES, } from '../../../core/types.js';
import { recordedLockedByLiveAd } from '../../../core/live-entry.js';
import { parseVastAd } from '../vast-ad.js';
function isAutoplayRejection(cause) {
    // 앱의 `auto-start.ts` 와 같은 판정 — 이름이 `NotAllowedError` 인가.
    return (typeof cause === 'object' &&
        cause !== null &&
        cause.name === 'NotAllowedError');
}
export class FlowerReceiver {
    channel;
    deviceId;
    storage;
    parseXml;
    onFallback;
    media;
    channelId;
    listeners = new Map();
    offerCards;
    onTimeUpdate = () => {
        // 규칙 85 — 바이패스 중에는 상품 카드가 없다. 그 경과는 라이브의 초라 회차의 큐와 섞으면 엉뚱한 카드가 뜬다.
        if (this.live !== null)
            return;
        this.offerCards.tick();
    };
    /** v37. 마지막 `liveChanged` — 바이패스 중이면 그 창 */
    live = null;
    state = 'idle';
    items = [];
    current = null;
    adBreak = null;
    paused = false;
    /** 첫 `playing` 을 봤는가 — 그 전의 실패만 폴백이다 */
    playedOnce = false;
    fellBack = false;
    stopped = false;
    offerCorrelator = 0;
    /** 정책이 요구했는데 열리지 않은 브레이크(Flower 몫 · 진단용 · 최근 20) */
    skippedBreaks = [];
    constructor(opts) {
        this.storage = opts.storage;
        this.parseXml = opts.parseXml;
        this.onFallback = opts.onFallback;
        this.media = opts.media;
        this.channelId = opts.channelId;
        this.deviceId = resolveDeviceId('', opts.storage);
        this.channel = new opts.FlowerChannel({
            serverUrl: opts.serverUrl,
            channelId: opts.channelId,
            deviceId: this.deviceId,
            authToken: opts.authToken,
            hls: opts.hls,
            lastAdBreakEndedAt: readLastBreakEndedAt(opts.storage),
            adsEnabled: opts.adsEnabled,
            adUi: 'none',
        });
        this.offerCards = new OfferCues(opts.offers ?? null, {
            policy: () => this.getOfferPolicy(),
            elapsedSec: () => this.getElapsedSec(),
            isPlaying: () => this.state === 'playing',
            inAdBreak: () => this.adBreak !== null,
            currentItem: () => this.current,
            requestContext: () => ({
                channelId: this.playlist()?.channelId ?? this.channelId,
                lang: this.getAdPolicy()?.language ?? 'ko',
                deviceId: this.deviceId,
                playlistId: this.getPlaylistId() ?? '',
            }),
            nextCorrelator: () => (this.offerCorrelator += 1),
            correlator: () => this.offerCorrelator,
            emit: (view) => {
                this.emit('offerChanged', view);
            },
        });
        this.wire();
        this.media.addEventListener('timeupdate', this.onTimeUpdate);
    }
    // ── 재생 ────────────────────────────────────────────────────────────
    async start(opts = {}) {
        this.offerCards.reset();
        await this.opening(() => this.channel.start(opts));
    }
    async selectItem(productId) {
        // 2026-10-29 · 라이브 앞뒤 광고 중 녹화물 잠금 — BLOOM 과 같은 판정이다(`recordedLockedByLiveAd` · `docs/45` §18.8)
        if (this.recordedLocked())
            return;
        await this.opening(() => this.channel.selectItem(productId));
    }
    async resync() {
        this.offerCards.reset();
        await this.opening(() => this.channel.resync());
    }
    async skip() {
        await this.guard(() => this.channel.skip(), '건너뛰기 전환이 실패했다.');
    }
    async skipAd() {
        await this.guard(() => this.channel.skipAd(), '광고 건너뛰기가 실패했다.');
    }
    pause() {
        this.channel.pause();
    }
    async resume() {
        try {
            await this.channel.resume();
        }
        catch (e) {
            // BLOOM 과 같다 — 재개 실패는 화면을 덮지 않는다(`receiver.ts` 의 `resume`).
            this.emit('error', { code: 'PLAYBACK_FAILED', message: '재개하지 못했다.', cause: e });
        }
    }
    seekBy(deltaSec) {
        this.channel.seekBy(deltaSec);
        // 떠 있던 상품 카드는 걷힌다 — 그 장면의 것이다(v35 D5 · 규칙 91).
        this.offerCards.close();
    }
    sendInteraction(type) {
        this.channel.sendInteraction(type);
    }
    stop() {
        if (this.stopped)
            return;
        this.stopped = true;
        this.media.removeEventListener('timeupdate', this.onTimeUpdate);
        this.offerCards.reset();
        this.channel.stop();
        if (this.paused) {
            this.paused = false;
            this.emit('pausedChanged', false);
        }
        this.setState('idle');
    }
    // ── 조회 ────────────────────────────────────────────────────────────
    getState() {
        return this.state;
    }
    getDeviceId() {
        return this.deviceId;
    }
    getPlaylistId() {
        return this.snapshot()?.playlistId ?? null;
    }
    getChannelKind() {
        return this.playlist()?.channelKind ?? null;
    }
    getResume() {
        return this.playlist()?.resume ?? null;
    }
    getAdPolicy() {
        return this.playlist()?.adPolicy ?? null;
    }
    getOfferPolicy() {
        return this.playlist()?.offerPolicy ?? null;
    }
    canSkip() {
        return this.snapshot()?.skip.canSkip ?? false;
    }
    secondsUntilSkippable() {
        const snap = this.snapshot();
        return snap === null ? null : snap.skip.secondsUntilSkippable;
    }
    /** 콘텐츠면 회차의 초, **광고 중엔 지금 광고의 초**(규칙 73 ③ · Flower 가 같은 뜻으로 낸다) */
    getElapsedSec() {
        return this.snapshot()?.elapsedSec ?? 0;
    }
    /** **광고 중에도 콘텐츠**(규칙 50) */
    getCurrentItem() {
        return this.current;
    }
    getAdBreak() {
        return this.adBreak;
    }
    getAdBreakElapsedSec() {
        return this.snapshot()?.adBreakElapsedSec ?? 0;
    }
    isPaused() {
        return this.paused;
    }
    // ── 상품 카드 ───────────────────────────────────────────────────────
    dismissOffer() {
        this.offerCards.dismiss();
    }
    getOfferRemainingSec() {
        return this.offerCards.remainingSec();
    }
    /** `useReceiver` 는 `offerChanged` 를 듣는다 — 이 게터는 BLOOM 과 같은 이름의 짝이다 */
    getOffer() {
        return this.offerCards.view();
    }
    // ── 라이브 — Flower 에 맡긴다(v37 · `docs/75` §5.2) ──────────────────────
    getLive() {
        return this.stopped ? null : this.channel.getLive();
    }
    getLiveWindows() {
        return this.stopped ? [] : this.channel.getLiveWindows().map(toLiveWindow);
    }
    async joinLive() {
        if (this.stopped)
            return;
        await this.channel.joinLive();
    }
    async resumeRecorded() {
        if (this.stopped)
            return;
        await this.channel.resumeRecorded();
    }
    /** 앱의 1초 시계 — 닫힘 판정만(요청 없음). 재생이 멈춰 `timeupdate` 가 없어도 돌아오게 한다 */
    checkLive() {
        if (this.stopped)
            return;
        this.channel.checkLive();
    }
    /** 앱의 1초 시계 — `GET …/live-events` 는 Flower 의 채널 서버 클라이언트가 한다(규칙 87) */
    async refreshLive() {
        if (this.stopped)
            return;
        await this.channel.refreshLive();
    }
    liveSwitchTarget() {
        return this.stopped ? null : this.channel.liveSwitchTarget();
    }
    recordedLocked() {
        return recordedLockedByLiveAd(this.getAdPolicy()?.liveAdLockRecorded ?? false, this.getAdBreak()?.kind);
    }
    /**
     * 조회 주기. Flower 의 값이다 — 서버 시계 보정을 거기서 하므로(`serverSkewMs`) 같은 「지금」으로 판정한다.
     * 산술은 contracts 의 `livePollIntervalMs` 를 벡터(`live-window`)로 맞춘 것이다(규칙 95).
     */
    livePollIntervalMs() {
        return this.stopped ? null : this.channel.livePollIntervalMs();
    }
    // ── 디버그 ──────────────────────────────────────────────────────────
    /**
     * 광고 진단. 판정은 Flower 안에 있어 **이 쪽이 아는 것만** 싣는다 — 발화한 컷(`firedCuePoints`)·대기(`pending`)는
     * Flower 의 몫이라 비워 두고, 열리지 않은 브레이크의 이유는 `flowerSnapshot().skippedBreaks` 가 갖는다.
     */
    adDebugSnapshot() {
        return {
            hasAdapter: true,
            policy: this.getAdPolicy(),
            inBreak: this.adBreak !== null,
            pending: false,
            firedCuePoints: [],
            lastBreakEndedAtMs: readLastBreakEndedAt(this.storage),
            gapInMemoryOnly: false,
            correlator: 0,
        };
    }
    offerDebugSnapshot() {
        return this.offerCards.debugSnapshot();
    }
    /** Flower 엔진만의 진단 — 디버그 패널·하네스가 읽는다(§5.5) */
    flowerSnapshot() {
        return {
            channel: this.snapshot(),
            playlistText: this.stopped ? null : this.channel.playlistText(),
            skippedBreaks: [...this.skippedBreaks],
        };
    }
    on(event, cb) {
        let set = this.listeners.get(event);
        if (set === undefined) {
            set = new Set();
            this.listeners.set(event, set);
        }
        set.add(cb);
    }
    // ── 안 ──────────────────────────────────────────────────────────────
    wire() {
        const ch = this.channel;
        ch.on('stateChanged', (state) => {
            if (state === 'playing')
                this.playedOnce = true;
            this.setState(state);
        });
        ch.on('playlistLoaded', (items) => {
            this.items = items;
            this.emit('playlistLoaded', this.items);
        });
        ch.on('itemChanged', (item, index) => {
            this.current = (this.items[index] ?? item);
            // BLOOM 과 같은 자리 — 회차가 바뀌면 상품 상태를 비우고, **실제로 시작하는 초**로 큐를 무장한다
            // (이어보기 지점 앞의 큐는 지나간 것 · `OfferCues.arm`). 프리롤 중의 예고면 그 초는 광고의 0 이다.
            this.offerCards.reset();
            if (this.adBreak === null)
                this.offerCards.arm(this.current, this.getElapsedSec());
            this.emit('itemChanged', this.current, index);
        });
        ch.on('adBreakChanged', (b) => {
            const opening = this.adBreak === null && b !== null;
            // 규칙 64 — 브레이크가 열리면 카드부터 걷는다(대기분까지).
            if (opening)
                this.offerCards.close();
            const closing = this.adBreak !== null && b === null;
            this.adBreak = b === null ? null : this.toView(b);
            this.emit('adBreakChanged', this.adBreak);
            // 프리롤 뒤의 첫 회차는 광고 중에 예고만 됐다 — 닫힐 때 그 회차의 큐를 지금 초로 무장한다.
            if (closing && this.current !== null && this.offerCards.debugSnapshot().activeCount === 0) {
                this.offerCards.arm(this.current, this.getElapsedSec());
            }
        });
        ch.on('adBreakEnded', (atMs) => {
            try {
                this.storage.set(RECEIVER_LAST_AD_BREAK_KEY, String(atMs));
            }
            catch {
                // 저장소가 막혔다 — 다음 시작에서 간격이 초기화된다. BLOOM 과 같은 강등이다.
            }
        });
        ch.on('adBreakSkipped', (reason, kind) => {
            this.skippedBreaks.push({ kind, reason });
            if (this.skippedBreaks.length > 20)
                this.skippedBreaks.shift();
        });
        ch.on('pausedChanged', (paused) => {
            this.paused = paused;
            this.emit('pausedChanged', paused);
        });
        ch.on('eventSent', (e) => {
            this.emit('eventSent', e);
        });
        ch.on('transition', (m) => {
            this.emit('transition', m);
        });
        ch.on('error', (e) => {
            this.handleError(e);
        });
        ch.on('liveChanged', (v) => {
            // BLOOM 의 `openLive` 와 같은 자리 — 바이패스가 열리면 카드부터 걷는다(규칙 64 의 모양 · 규칙 85).
            // 복귀 뒤의 무장은 Flower 가 곧이어 내는 `itemChanged` 가 한다(위 · 적재한 초부터).
            if (v !== null)
                this.offerCards.close();
            this.live = v;
            this.emit('liveChanged', v);
        });
        ch.on('liveWindowsChanged', (windows) => {
            this.emit('liveWindowsChanged', windows.map(toLiveWindow));
        });
    }
    /**
     * 재생을 여는 셋(`start`·`selectItem`·`resync`)의 공통 꼬리. Flower 는 거절을 Promise 로 낸다(§6 의 구현 표).
     */
    async opening(run) {
        try {
            await run();
        }
        catch (e) {
            this.handleError(asChannelError(e));
        }
    }
    async guard(run, message) {
        try {
            await run();
        }
        catch (e) {
            this.handleError({ ...asChannelError(e), message });
        }
    }
    handleError(e) {
        if (this.stopped || this.fellBack)
            return;
        if (!this.playedOnce && !isAutoplayRejection(e.cause) && this.onFallback !== undefined) {
            this.fallBack(`start-failed:${e.code}`, e);
            return;
        }
        const code = RECEIVER_ERROR_CODES.includes(e.code)
            ? e.code
            : 'PLAYBACK_FAILED';
        // BLOOM 의 `fail()` 과 같은 모양 — 상태를 먼저 내리고 알린다. 복구되는 실패(`TRANSITION_ABANDONED`)는 상태를 그대로 둔다.
        if (code !== 'TRANSITION_ABANDONED')
            this.setState('error');
        this.emit('error', { code, message: e.message, cause: e.cause });
    }
    fallBack(reason, cause) {
        this.fellBack = true;
        this.onFallback?.(reason, cause);
    }
    toView(b) {
        const raw = b.ads[b.index];
        const item = (raw && parseVastAd(raw.vastXml, this.parseXml)) ?? degradedAdItem(raw);
        return {
            kind: b.kind,
            totalSec: b.totalSec,
            index: b.index,
            count: b.count,
            item,
            resumeAtSec: b.resumeAtSec,
        };
    }
    snapshot() {
        if (this.stopped)
            return null;
        return this.channel.snapshot();
    }
    playlist() {
        return (this.snapshot()?.playlist ?? null);
    }
    setState(next) {
        if (this.state === next)
            return;
        this.state = next;
        this.emit('stateChanged', next);
    }
    emit(event, ...args) {
        const set = this.listeners.get(event);
        if (!set)
            return;
        for (const cb of set) {
            cb(...args);
        }
    }
}
function asChannelError(e) {
    if (typeof e === 'object' && e !== null && 'code' in e) {
        const err = e;
        const message = typeof err.message === 'string' ? err.message : '';
        return { code: String(err.code), message, cause: err.cause };
    }
    return { code: 'PLAYBACK_FAILED', message: String(e), cause: e };
}
/**
 * 원문을 읽지 못한 광고 — **조용하지 않게** 강등한다(규칙 54). 광고 화면은 서야 하므로(그 자리에 회차 버튼이 그려지면
 * 「건너뛰기」가 회차를 넘긴다) 길이·id 만 있는 `AdItem` 을 만들고 구매는 웹 랜딩도 없는 상태로 둔다.
 */
function degradedAdItem(raw) {
    const id = /<Ad\s[^>]*\bid="([^"]+)"/.exec(raw?.vastXml ?? '')?.[1] ?? '';
    console.warn('[FlowerReceiver] 광고 원문을 읽지 못해 강등한다', id);
    const empty = [];
    return {
        creativeId: id,
        title: '',
        videoUrl: '',
        durationSec: raw?.durationSec ?? 0,
        skipOffsetSec: null,
        clickThrough: '',
        handoff: {
            productId: id,
            title: '',
            price: 0,
            thumbnailUrl: '',
            advertiserId: null,
            couponCode: null,
            handoffChannelId: null,
            landingUrl: '',
        },
        handoffSource: 'clickthrough',
        tracking: {
            impression: empty,
            start: empty,
            firstQuartile: empty,
            midpoint: empty,
            thirdQuartile: empty,
            complete: empty,
            skip: empty,
            click: empty,
        },
    };
}
/**
 * 2026-10-29 · **Flower 가 넘긴 창을 계약의 모양으로 채운다.** Flower 의 JSON 은 기본값을 싣지 않는다(`encodeDefaults = false` ·
 * `explicitNulls = false`) — `startMode`·`endMode` 가 `'time'` 이면, `actualStartAtMs`·`actualEndAtMs` 가 `null` 이면, `title` 이 비었으면 그 칸이
 * **아예 없다.** 그대로 넘기면 `startMode === 'time'` 을 묻는 자리가 조용히 거짓이다 — 편성표가 시각 방식 이벤트의 시작 전 광고 띠를 그리지
 * 않았다(현재 채널의 라이브 칸은 리시버의 창으로 갈아 끼우므로, 열 때는 있다가 첫 조회 뒤 사라졌다 · `docs/45` §18.8).
 */
export function toLiveWindow(raw) {
    const w = raw;
    return {
        eventId: w.eventId,
        title: w.title ?? '',
        startAtMs: w.startAtMs,
        endAtMs: w.endAtMs,
        startMode: w.startMode ?? 'time',
        endMode: w.endMode ?? 'time',
        actualStartAtMs: w.actualStartAtMs ?? null,
        actualEndAtMs: w.actualEndAtMs ?? null,
        streamUrl: w.streamUrl ?? '',
    };
}
//# sourceMappingURL=flower-receiver.js.map